import React from "react";
import useInbox from "hooks/useInbox";
import CustomPopup from "components/Pages/Inbox/Popups/CustomPopup";
import Tooltip from "components/Elements/Tooltip/Tooltip";
import useOnClickOutside from "hooks/useOnClickOutside";
import Icon from "components/Elements/Icon/Icon";

import { memo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectItemsPerPage } from "store/selectors/inboxSelectors";
import { useContent } from "contexts/contentContext/contentContext";

import styles from "./itemsPerPage.module.scss";

const VALUES = [10, 20, 50, 100] as const;
const DEFAULT_ICON_NAME = "newinbox/arrow_down";
const DEFAULT_POPUP_OFFSET_TOP = 30;
const FIRST_PAGE_NUM = 1;

interface ItemsPerPageI
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  overrideClassName?: string;
  iconName?: string;
  textKey?: string;
  tooltipTextKey?: string;
  popupOffsetTop?: number;
  onChange?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
}

/**
 * Component to show items per page and change it based on redux store value and url params
 */
const ItemsPerPage = memo((props: ItemsPerPageI) => {
  const {
    overrideClassName,
    onClick,
    onChange,
    iconName = DEFAULT_ICON_NAME,
    textKey = "show",
    tooltipTextKey = "show-items-per-page-tooltip",
    popupOffsetTop = DEFAULT_POPUP_OFFSET_TOP,
    ...restProps
  } = props;
  const { getTextByKey } = useContent();
  const ref = useRef(null);

  const itemsPerPage = useSelector(selectItemsPerPage);
  const { setItemsPerPage, setPageNumber } = useInbox();

  const [showPopup, setShowPopup] = useState(false);

  useOnClickOutside(ref, () => setShowPopup(false));

  /**
   * Change items per page value and close popup
   */
  const handleChange = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    // Get the value from the clicked element
    const value = +e.currentTarget.textContent! as (typeof VALUES)[number];

    // Ignore current selected value
    if (value === itemsPerPage) {
      return;
    }

    setItemsPerPage(value);
    setShowPopup(false);

    // Reset current page number
    setPageNumber(FIRST_PAGE_NUM);

    // Call the callback function if it exists
    onChange?.(e);
  };

  /**
   * Show/hide popup
   */
  const handleShowPopup = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setShowPopup(!showPopup);

    // Call the callback function if it exists
    onClick?.(e);
  };

  return (
    <div
      {...restProps}
      className={
        overrideClassName || `${styles.wrapper} ${restProps.className || ""}`
      }
    >
      {/*@ts-ignore*/}
      <Tooltip id={tooltipTextKey} tooltipTextKey={tooltipTextKey} start>
        <span className={styles.text}>{getTextByKey(textKey)}</span>
        <span className={styles.block} ref={ref} onClick={handleShowPopup}>
          <span>{itemsPerPage}</span>
          <Icon name={iconName} iconClass={styles[showPopup ? "up" : "down"]} />
          <CustomPopup showState={showPopup} topPosition={popupOffsetTop}>
            <ul className={styles.popupList}>
              {VALUES.map((value) => (
                <li
                  className={itemsPerPage === value ? styles.selected : ""}
                  key={value}
                  onClick={handleChange}
                >
                  {value}
                </li>
              ))}
            </ul>
          </CustomPopup>
        </span>
      </Tooltip>
    </div>
  );
});

export default ItemsPerPage;
